// Home, served at `/` by the glob router in src/App.tsx.
//
// Family-facing landing page. The app's own screens are still landing module by
// module, so this page does two jobs: explain what SPZ is to a relative who
// followed a link, and point them at the GitHub issue templates, which work
// today and are how we collect intake before the real form exists.
//
// It also keeps a quiet self-check at the bottom: the routes the router actually
// mounted plus a live call to the `health` edge function, so a deployed build
// proves its own wiring without anyone opening a terminal.
import { useEffect, useState } from 'react'
import { listRoutes } from '../App'
import {
  FUNCTIONS_URL,
  SUPABASE_PUBLISHABLE_KEY,
  isSupabaseConfigured,
} from '../lib/supabaseClient'

type Health = { ok: boolean; env_present?: string[] }

const REPO = 'https://github.com/saltz-party-zone/spz'

// One button, not two. This used to offer "Add your household" and "Add a house
// requirement" separately, which made sense when each opened its own GitHub issue
// template. Both now land on /intake — requirements are section (e) of the same
// form, because `house_requirements_raw` is a column on `household` and there is
// no separate requirement entity to send anyone to. Two buttons to one page reads
// as a bug whether or not it is one.
//
// A second entry point earns its place again when requirements get their own
// table and screen — see #36, which asks for required vs nice-to-have and is the
// `criterion.kind = hard | soft` work CLAUDE.md describes.
const INTAKE = '/intake'

export default function HomePage() {
  const [health, setHealth] = useState<Health | null>(null)
  const [error, setError] = useState<string | null>(null)
  const routes = listRoutes().filter((r) => r !== '/')

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setError('VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY are not set for this build.')
      return
    }
    let cancelled = false
    fetch(`${FUNCTIONS_URL}/health`, {
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
    })
      .then((res) => (res.ok ? (res.json() as Promise<Health>) : Promise.reject(res.status)))
      .then((body) => {
        if (!cancelled) setHealth(body)
      })
      .catch((cause) => {
        if (!cancelled) setError(`health check failed (${String(cause)})`)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <main className="shell">
      <header className="hero">
        <p className="eyebrow">Saltz&rsquo;s Party Zone</p>
        <h1>One trip. Everybody there.</h1>
        <p className="lede">
          Every year the same problem: nobody knows where to go, everyone has different dates,
          and by the time it&rsquo;s sorted the good houses are gone. SPZ collects everyone&rsquo;s
          constraints once, then works out <strong>where</strong>, <strong>when</strong>, and{' '}
          <strong>which house</strong>.
        </p>
      </header>

      <section className="card">
        <h2>Start here</h2>
        <p>
          Six questions: where you&rsquo;re coming from, how far you&rsquo;ll drive, how
          you&rsquo;d travel, when you&rsquo;re free, what the house needs, and what you want
          nearby. Answers save
          themselves as you go &mdash; there&rsquo;s no submit button to forget.
        </p>
        <div className="actions">
          <a className="btn primary" href={INTAKE}>
            Add your household
          </a>
        </div>
        <p className="fine">
          About three minutes. We ask for your <strong>town and ZIP</strong>, never a street
          address. You&rsquo;ll sign in with a link we email you &mdash; no password to make up.
          Use the address the family knows you by; that&rsquo;s how we recognize you.
        </p>
      </section>

      <section>
        <h2>How it decides</h2>
        <dl className="rules">
          <dt>Everybody together beats everybody comfortable</dt>
          <dd>
            A window where all of us overlap for two days wins over a longer one where most
            people make it. Partial days on either end are fine, just not the goal.
          </dd>
          <dt>Nowhere is picked by averaging</dt>
          <dd>
            Regions are scored by the travel burden on the <em>worst-off</em> household. An
            average quietly picks somewhere brutal for one family.
          </dd>
          <dt>The table has to fit us</dt>
          <dd>
            Nine-bedroom houses routinely have six-person dining tables. No rental site lets
            you filter on that. This one will.
          </dd>
        </dl>
      </section>

      <section className="card">
        <h2>Where it stands</h2>
        <p>
          These update as households fill in the form. Everyone can see them &mdash; that way
          nobody suggests a place that&rsquo;s already on the list.
        </p>
        <div className="actions">
          <a className="btn" href="/regions">
            Places being considered
          </a>
          <a className="btn" href="/windows">
            Dates that work
          </a>
        </div>
        <p className="fine">
          Organizing? <a href="/trips">Trip setup</a> is where households, people and who can
          join are managed.
        </p>
      </section>

      <section className="card">
        <h2>Something broken, or missing?</h2>
        <p>
          Tell us. It goes straight to the people building this &mdash; nothing to sign up
          for, no account to make.
        </p>
        <div className="actions">
          <a className="btn" href="/feedback">
            Report a bug or ask for a feature
          </a>
        </div>
      </section>

      <footer className="status">
        <h2>Build status</h2>
        <p className="fine">
          {routes.length > 0 ? (
            <>{routes.length} screens deployed.</>
          ) : (
            <>No screens deployed yet &mdash; modules are landing one at a time.</>
          )}
        </p>
        <p className="fine">
          {health ? (
            <>Backend ok &mdash; {health.env_present?.length ?? 0} secrets configured.</>
          ) : (
            <>{error ?? 'checking backend…'}</>
          )}
        </p>
        <p className="fine">
          <a href={REPO}>Source and open work</a> &middot;{' '}
          <a href="/extrafun">Extra fun</a>
        </p>
      </footer>
    </main>
  )
}
